import { Component, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { of, pluck, switchMap } from 'rxjs';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { BpService } from '../../../core/services/bp.service';
import { KkmsService } from '../../../core/services/kkms.service';
import { AlertsService } from '../../../core/services/alerts.service';
import {
  RegisterKkmFields,
  RegisterKkmForm,
  RegisterKkmSignatureForm,
} from './register-kkm.model';
import { SkipPreloader } from '../../../core/services/loader.service';
import { KkmsPageState } from '../kkms-page/kkms-page.state';
import { KkmProcessService } from '../shared/services/kkm-process.service';
import { PreviousPage } from '../../../core/models/bp.model';
import { Pages } from '../../pages';

@UntilDestroy()
@Component({
  selector: 'tax-new-kkm',
  templateUrl: './new-kkm.component.html',
  styleUrls: ['./new-kkm.component.sass'],
})
export class NewKkmComponent implements OnInit {
  processForm?: RegisterKkmForm | RegisterKkmSignatureForm;
  isChangingOfd = false;
  kkmRegisterMethodName = '';
  constructor(
    private alertsService: AlertsService,
    private route: ActivatedRoute,
    private router: Router,
    private bpService: BpService,
    private kkmsService: KkmsService,
    private retailPlacesPageState: KkmsPageState,
    private kkmProcessService: KkmProcessService,
  ) {}

  ngOnInit(): void {
    this.isChangingOfd = Boolean(this.route.snapshot.queryParams.isChangingOfd);
    this.kkmRegisterMethodName = this.isChangingOfd ? 'new.kkm.transition.ofd.full.title' : 'add.kkm.title';
    this.initRegisterProcess();
  }

  continueProcess(kkmForm: RegisterKkmFields): void {
    kkmForm.continueRegistration
      ? this.goToSignStep(kkmForm)
      : this.saveKkm(kkmForm);
  }

  goToFirstStep(): void {
    this.bpService.goToStepAndGetForm<RegisterKkmForm>(
      'inisBackToRegistrationFormMessage',
      `/main/kkms/register-kkm/${this.route.snapshot.params.kkmId || ''}`,
      {},
    )
      .pipe(untilDestroyed(this))
      .subscribe((processForm) => {
        this.processForm = processForm;
      });
  }

  private getRegisterKkmForm(): void {
    this.route.params
      .pipe(
        pluck('kkmId'),
        switchMap(kkmId => (kkmId
          ? this.bpService.startBpAndGetData('inisRegistrationSavedKkmMessage', {
            kkmId,
          }, {}, SkipPreloader.true)
          : this.bpService.startBpAndGetData('inisRegistrationNewKkmMessage', {}, {}, SkipPreloader.true))),
        switchMap((processForm) => {
          this.processForm = (processForm as RegisterKkmForm | RegisterKkmSignatureForm);
          return processForm.fields.declineReason
            ? this.kkmsService.getDeclineReason(processForm.fields.declineReason)
            : of(true);
        }),
        untilDestroyed(this),
      )
      .subscribe();
  }

  private goToSignStep(kkmForm: RegisterKkmFields): void {
    this.kkmProcessService.processKkmFormWithRcaCheck<RegisterKkmSignatureForm, RegisterKkmFields>(kkmForm)
      .pipe(untilDestroyed(this))
      .subscribe(
        (processForm) => {
          this.retailPlacesPageState.resetAllStates();
          this.showSuccessAlert();
          this.processForm = processForm;
        },
      );
  }

  private saveKkm(kkmForm: RegisterKkmFields): void {
    this.bpService.submitCurrentBp(kkmForm)
      .pipe(untilDestroyed(this))
      .subscribe(() => {
        this.retailPlacesPageState.resetAllStates();
        this.router.navigate([Pages.Kkms]);
      });
  }

  private initRegisterProcess(): void {
    const savedKkmForm = (history?.state as PreviousPage<RegisterKkmFields>)?.state;
    // Условие для продолжения БП регистрации кассы после корректировки адреса и РКА ТТ
    if (savedKkmForm) {
      if (history?.state?.isUpdatedRca) {
        this.bpService.submitAndGetForm<RegisterKkmSignatureForm>(savedKkmForm)
          .pipe(untilDestroyed(this))
          .subscribe((processForm) => {
            this.retailPlacesPageState.resetAllStates();
            this.showSuccessAlert();
            this.processForm = processForm;
          });
      } else {
        this.processForm = {
          ...this.processForm, formKey: 'kkm-registration-manually', fields: savedKkmForm,
        };
      }
    } else this.getRegisterKkmForm();
  }

  private showSuccessAlert(): void {
    this.alertsService.add({
      type: 'success',
      message: 'kkm.registration.save.success.alert',
      title: 'kkm.action.successfully.alert.title',
    });
  }
}
